import { createSlice } from "@reduxjs/toolkit";

import { advisorApi } from "@services";
import { IAdvisorChoice, IAdvisorEntry, IAdvisorNodePayload } from "@types";

/** The advisor's sentinel node_id for a finished conversation. */
export const ADVISOR_TERMINAL_NODE = "COMPLETE";

interface IAdvisorState {
  sessionId: string | null;
  entries: IAdvisorEntry[];
  choices: IAdvisorChoice[];
  // Snapshot of `choices` taken right before an optimistic update, restored if the request fails.
  restoreChoices: IAdvisorChoice[];
  // A turn that arrived with a thinking sequence; held back until the reveal animation finishes.
  pending: IAdvisorNodePayload | null;
  busy: boolean;
  loading: boolean;
  error: string | null;
  draft: string;
}

const initialState: IAdvisorState = {
  sessionId: null,
  entries: [],
  choices: [],
  restoreChoices: [],
  pending: null,
  busy: false,
  loading: false,
  error: null,
  draft: "",
};

const advisorSlice = createSlice({
  name: "advisor",
  initialState,
  reducers: {
    setAdvisorDraft: (state, action: { payload: string }) => {
      state.draft = action.payload;
    },
    setAdvisorLoading: (state, action: { payload: boolean }) => {
      state.loading = action.payload;
    },
    restoreAdvisorSession: (
      state,
      action: {
        payload: {
          sessionId: string;
          entries: IAdvisorEntry[];
          choices: IAdvisorChoice[];
        };
      }
    ) => {
      state.sessionId = action.payload.sessionId;
      state.entries = action.payload.entries;
      state.choices = action.payload.choices;
      state.loading = false;
    },
    revealPendingAdvisorTurn: state => {
      if (!state.pending) return;
      state.entries.push({ role: "advisor", payload: state.pending });
      state.choices = state.pending.choices;
      state.pending = null;
    },
    resetAdvisorSession: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        advisorApi.endpoints.startAdvisorSession.matchPending,
        state => {
          state.busy = true;
          state.error = null;
        }
      )
      .addMatcher(
        advisorApi.endpoints.startAdvisorSession.matchFulfilled,
        (state, { payload }) => {
          state.busy = false;
          state.sessionId = payload.session_id;
          state.entries = [{ role: "advisor", payload: payload.turn }];
          state.choices = payload.turn.choices;
        }
      )
      .addMatcher(
        advisorApi.endpoints.startAdvisorSession.matchRejected,
        (state, { payload }) => {
          state.busy = false;
          state.error =
            payload?.message || "Could not start the advisory session.";
        }
      )
      .addMatcher(
        advisorApi.endpoints.chooseAdvisorChoice.matchPending,
        (state, action) => {
          const { choice_id, text } = action.meta.arg.originalArgs;
          const label =
            text ??
            state.choices.find(choice => choice.choice_id === choice_id)
              ?.label ??
            "";

          state.error = null;
          state.busy = true;
          state.restoreChoices = state.choices;
          state.choices = [];
          state.entries.push({ role: "user", text: label });
          if (text !== undefined) state.draft = "";
        }
      )
      .addMatcher(
        advisorApi.endpoints.chooseAdvisorChoice.matchFulfilled,
        (state, { payload, meta }) => {
          // A reset may have torn down this session while the request was in flight — applying
          // a late response now would splice a stale turn into the new session.
          if (meta.arg.originalArgs.session_id !== state.sessionId) return;

          state.busy = false;
          state.sessionId = payload.session_id;
          const turn = payload.turn;

          if (turn.thinking_sequence && turn.thinking_sequence.length > 0) {
            state.pending = turn;
          } else {
            state.entries.push({ role: "advisor", payload: turn });
            state.choices = turn.choices;
          }
        }
      )
      .addMatcher(
        advisorApi.endpoints.chooseAdvisorChoice.matchRejected,
        (state, action) => {
          if (action.meta.arg.originalArgs.session_id !== state.sessionId)
            return;

          state.busy = false;
          state.error =
            action.payload?.message || "That step failed. Try again.";
          state.entries.pop();
          state.choices = state.restoreChoices;

          const { text } = action.meta.arg.originalArgs;
          if (text !== undefined) state.draft = text;
        }
      )
      .addMatcher(
        advisorApi.endpoints.fetchAdvisorSession.matchPending,
        state => {
          state.error = null;
        }
      )
      .addMatcher(
        advisorApi.endpoints.fetchAdvisorSession.matchFulfilled,
        (state, { payload }) => {
          // A mismatch means the conversation moved on (a reset, or another offer) while this
          // read was in flight; applying it would repoint later `choose` calls at a dead
          // session. The null check allows the cold bootstrap, where this response sets the id.
          if (state.sessionId && payload.session_id !== state.sessionId) return;

          state.loading = false;
          state.sessionId = payload.session_id;

          // A cached transcript (restoreAdvisorSession) already seeded `entries`; this call only
          // confirms the session is still valid and refreshes the choices it currently offers.
          // Only fall back to reconstructing a 1-entry transcript when there's no cache.
          if (state.entries.length === 0 && payload.turn) {
            state.entries = [{ role: "advisor", payload: payload.turn }];
          }
          state.choices =
            payload.status === "ACTIVE" && payload.turn
              ? payload.turn.choices
              : [];
        }
      )
      .addMatcher(
        advisorApi.endpoints.fetchAdvisorSession.matchRejected,
        state => {
          // A stale cached session_id (expired/gone). useAdvisorSession treats this as "not
          // found" and starts a fresh session, so surfacing an error here would just flash red
          // right before that recovery kicks in.
          state.loading = false;
        }
      );
  },
});

export const {
  setAdvisorDraft,
  setAdvisorLoading,
  restoreAdvisorSession,
  revealPendingAdvisorTurn,
  resetAdvisorSession,
} = advisorSlice.actions;

export default advisorSlice.reducer;
